/**
 * Shared contracts for the Tabbio Apply browser extension.
 *
 * Everything here crosses the wire between the extension and `apps/api`, so it
 * deliberately carries no `chrome.*` vocabulary. Extension-internal message
 * shapes live in `apps/extension/src/messaging/protocol.ts` instead.
 *
 * Runtime values in this file are re-exported through `extension.runtime.js`
 * so the barrel stays importable from Node without extension specifiers.
 */

export const JOB_EXTRACTION_METHODS = [
  'jsonld',
  'adapter',
  'heuristic',
] as const;

export type JobExtractionMethod = (typeof JOB_EXTRACTION_METHODS)[number];

export const JOB_WORK_MODES = ['onsite', 'remote', 'hybrid'] as const;

export type JobWorkMode = (typeof JOB_WORK_MODES)[number];

/**
 * A job posting read out of the page the user is looking at.
 *
 * `confidence` drives the panel's tone: at or above CONFIDENT_JOB_THRESHOLD we
 * assert "this is a job", below it we ask.
 */
export interface ScrapedJob {
  /** Schema version, so the server can migrate old panels gracefully. */
  v: 1;
  url: string;
  canonicalUrl?: string | null;
  /** Adapter id that produced this, for example 'linkedin' or 'generic'. */
  site: string;
  /** Detected applicant tracking system family, when we can tell. */
  ats?: string | null;
  title: string;
  companyName?: string | null;
  location?: string | null;
  workMode?: JobWorkMode | null;
  employmentType?: string | null;
  salaryRaw?: string | null;
  /** Plain text, HTML stripped and length capped. */
  description: string;
  requirements?: string[] | null;
  postedAt?: string | null;
  /** Board or ATS identifier parsed out of the URL when available. */
  externalId?: string | null;
  /** BCP 47-ish language tag detected from the page, for example 'en' or 'ar'. */
  language?: string | null;
  extractedVia: JobExtractionMethod;
  /** 0 to 1. */
  confidence: number;
  capturedAt: string;
}

export const CONFIDENT_JOB_THRESHOLD = 0.65;

// ---------------------------------------------------------------------------
// Form extraction and autofill
// ---------------------------------------------------------------------------

export const FORM_FIELD_KINDS = [
  'text',
  'email',
  'tel',
  'url',
  'number',
  'date',
  'textarea',
  'select',
  'radio',
  'checkbox',
  'file',
  'combobox',
] as const;

export type FormFieldKind = (typeof FORM_FIELD_KINDS)[number];

export interface FormFieldOption {
  value: string;
  label: string;
}

export interface ExtensionFormField {
  /** Stable within one extraction pass; used to address the field on fill. */
  id: string;
  kind: FormFieldKind;
  /** Best resolved human label, following the label priority chain. */
  label: string;
  /** Nearest fieldset legend or section heading, for disambiguation. */
  section?: string | null;
  /** Raw `autocomplete` attribute when the site sets one. */
  autocomplete?: string | null;
  /**
   * Raw `accept` attribute on a file input.
   *
   * Load bearing: a form can carry several file inputs and only one of them
   * takes a CV. Workable puts an image-only "Photo" dropzone above the resume
   * field, and attaching a PDF there fails visibly on the page.
   */
  accept?: string | null;
  /** Normalized name and id tokens used by the deterministic mapper. */
  tokens: string[];
  placeholder?: string | null;
  required: boolean;
  maxLength?: number | null;
  options?: FormFieldOption[] | null;
  /** True when the control lives in a frame other than the top document. */
  inFrame?: boolean;
}

export interface ExtensionFormSchema {
  formId: string;
  /** 1-based step index for multi-step wizards, null for single-page forms. */
  step?: number | null;
  totalSteps?: number | null;
  fields: ExtensionFormField[];
  /** Origins of job-form frames we could see but are not permitted to script. */
  blockedFrameOrigins?: string[];
}

/**
 * Versioned, least-privilege profile shape used by Application Assist.
 *
 * This deliberately excludes protected-trait and legal-identity fields. Those
 * answers stay user-controlled and are never needed by the deterministic
 * contact/experience mapper running in a browser surface.
 */
export interface ExtensionApplicationProfile {
  v: 1;
  name: string;
  headline: string;
  location: string;
  contactEmail: string;
  phone: string;
  website: string;
  linkedin: string;
  github: string;
  summary: string;
  experiences: Array<{
    title: string;
    company: string;
    startDate: string | null;
    endDate: string | null;
    isCurrent: boolean;
  }>;
}

export type ApplicationAssistFieldDisposition =
  | 'exact'
  | 'review'
  | 'user_only'
  | 'blocked';

export type ApplicationAssistFieldReason =
  | 'exact_profile_value'
  | 'generated_draft'
  | 'protected_trait'
  | 'legal_consent'
  | 'work_authorization'
  | 'criminal_history'
  | 'sensitive_identifier'
  | 'authentication_secret'
  | 'captcha'
  | 'unsupported_attachment';

export interface ApplicationAssistFieldPolicy {
  disposition: ApplicationAssistFieldDisposition;
  reason: ApplicationAssistFieldReason;
  /** Whether this field may be included in an AI request. */
  allowAi: boolean;
  /** Whether Application Assist may write a reviewed value into the page. */
  allowWrite: boolean;
  requiresReview: boolean;
}

export interface ApplicationAssistManualField {
  fieldId: string;
  label: string;
  disposition: Extract<
    ApplicationAssistFieldDisposition,
    'user_only' | 'blocked'
  >;
  reason: ApplicationAssistFieldReason;
}

export const FIELD_VALUE_SOURCES = ['profile', 'cv', 'ai', 'user'] as const;

export type FieldValueSource = (typeof FIELD_VALUE_SOURCES)[number];

export interface AutofillFieldPlan {
  fieldId: string;
  value: string;
  source: FieldValueSource;
  /** 0 to 1. Low confidence values are pre-flagged for review. */
  confidence: number;
  /** Safety classification applied before the value was planned. */
  policy?: ApplicationAssistFieldPolicy;
  /** True for generated/defaulted values that need an explicit scan. */
  requiresReview?: boolean;
  /** Set for file inputs: which CV to attach and under what filename. */
  attachment?: { cvId: string; filename: string } | null;
}

export interface AutofillPlan {
  formId: string;
  fields: AutofillFieldPlan[];
}

export const AUTOFILL_FIELD_STATUSES = [
  'filled',
  'skipped',
  'needs_review',
  'failed',
] as const;

export type AutofillFieldStatus = (typeof AUTOFILL_FIELD_STATUSES)[number];

export interface AutofillFieldResult {
  fieldId: string;
  status: AutofillFieldStatus;
  /** Machine-readable reason, for example 'no_match' or 'upload_timeout'. */
  reason?: string | null;
}

// ---------------------------------------------------------------------------
// API payloads
// ---------------------------------------------------------------------------

export const FIT_VERDICTS = ['strong', 'good', 'stretch', 'weak'] as const;

export type FitVerdict = (typeof FIT_VERDICTS)[number];

export interface ExtensionFitCheckResult {
  /** 0 to 100. */
  score: number;
  verdict: FitVerdict;
  strengths: string[];
  gaps: string[];
  suggestions: string[];
  /** Set when the scraped job matched a posting already in the Tabbio catalog. */
  catalogJobId: string | null;
}

export interface ExtensionTailorDiffEntry {
  section: string;
  label: string;
  changed: boolean;
  before?: string | null;
  after?: string | null;
}

export interface ExtensionTailorResult {
  cvId: string;
  title: string;
  displayTitle: string;
  /** True when this call created the tailored CV rather than reusing one. */
  tailoringCreated: boolean;
  targetJobLabel: string | null;
  diff: ExtensionTailorDiffEntry[];
}

/**
 * One unfilled form field, sent to the model for an answer.
 *
 * Every control kind except `file` is askable: the model picks real option
 * labels for selects and radios, returns "true"/"false" for checkboxes, and
 * YYYY-MM-DD for dates. File inputs stay on the attachment path instead.
 */
export interface ExtensionAssistQuestion {
  id: string;
  label: string;
  section?: string;
  autocomplete?: string;
  tokens?: string[];
  kind: Exclude<FormFieldKind, 'file'>;
  options?: string[];
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
}

export interface ExtensionAssistAnswer {
  id: string;
  value: string;
  /** 0 to 1. Rendered as a confidence chip on the review screen. */
  confidence: number;
}

export interface ExtensionApplyAssistResult {
  answers: ExtensionAssistAnswer[];
  coverLetter: string | null;
}

export const EXTENSION_APPLY_OUTCOMES = ['submitted', 'not_applied'] as const;

export type ExtensionApplyOutcome = (typeof EXTENSION_APPLY_OUTCOMES)[number];

export interface ExtensionApplyIntentResult {
  intentId: string;
  catalogJobId: string;
}

// ---------------------------------------------------------------------------
// Remote adapter packs
// ---------------------------------------------------------------------------

/**
 * Declarative extraction recipe. These are data, not code, which is what keeps
 * remote pack delivery inside the MV3 remote-code policy.
 */
export interface JobSiteAdapter {
  id: string;
  /** Match patterns tested against the page URL. Supports `*` wildcards. */
  urlPatterns: string[];
  ats?: string | null;
  /** Ordered CSS selector candidates per field; first non-empty result wins. */
  fields: {
    title?: string[];
    company?: string[];
    location?: string[];
    description?: string[];
    employmentType?: string[];
    salary?: string[];
  };
  /** Parses a stable posting id out of the URL. */
  externalId?: { source: 'url'; regex: string } | null;
  /**
   * Parses the employer out of the URL, for boards that never print it in the
   * page. Greenhouse's job-boards layout is the common case: the posting shows
   * the role and location but the company only appears in the path slug.
   */
  companyFromUrl?: { regex: string } | null;
  /** Selectors that must be clicked before reading, for example "See more". */
  expandSelectors?: string[];
  /** Per-site hints for the autofill writer, keyed by our canonical field key. */
  fieldHints?: Record<string, string[]>;
}

export interface AdapterPack {
  version: string;
  adapters: JobSiteAdapter[];
}
